import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { loginWithPassword as apiLogin, logout as apiLogout } from "@/lib/auth/actions";

// Types that match your backend JWT payload (adjust as needed)
export interface AuthUser {
  id: number;
  role: "customer" | "staff" | "admin";
  name: string;
  email: string | null;
  phone: string | null;
}

export interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  loginWithPassword: (email: string, password: string) => Promise<{ success: boolean; error?: string; redirectTo?: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>; // refetch /api/auth/me
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Optional API base for direct calls; keep empty in dev to use Vite proxy
const API_BASE = (import.meta as any)?.env?.VITE_API_BASE || "";

// Build URL safely
function buildUrl(path: string) {
  if (!API_BASE) return path;
  const base = API_BASE.replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

async function safeParseJson(res: Response, ctx: string) {
  try {
    const data = await res.json();
    console.log(`[AuthCtx] ${ctx} parsed JSON`, data);
    return data;
  } catch (err) {
    console.warn(`[AuthCtx] ${ctx} JSON parse failed`, err);
    const text = await res.text().catch(() => "");
    return { _text: text || "" };
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const refresh = useCallback(async () => {
    const url = buildUrl("/api/auth/me");
    console.log("[AuthCtx] refresh /api/auth/me", { url });
    try {
      const res = await fetch(url, { credentials: "same-origin" });
      const data: any = await safeParseJson(res, "me");
      console.log("[AuthCtx] me response", { status: res.status });

      if (res.ok && data?.user) {
        const u = data.user as AuthUser;
        setUser(u);
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error("[AuthCtx] refresh error", err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initial load
    refresh();
  }, [refresh]);

  const loginWithPassword: AuthContextType["loginWithPassword"] = async (email, password) => {
    console.log("[AuthCtx] loginWithPassword start");
    const result = await apiLogin(email, password);
    console.log("[AuthCtx] loginWithPassword result", result);
    if (result.success) {
      // Get the current user after successful login
      await refresh();
    }
    return result;
  };

  const logout: AuthContextType["logout"] = async () => {
    console.log("[AuthCtx] logout start");
    await apiLogout();
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    loginWithPassword,
    logout,
    refresh
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}